import { ArrowLeft, ArrowUpCircle, Check, Loader2, Moon, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { PRIORITIES } from "@handoverpro/shared";
import type { Priority } from "@handoverpro/shared";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { PatientResponseDto } from "~/lib/api";
import {
  acknowledgePatient,
  changePriority,
  completePatient,
  completeTask,
  escalatePatient,
  escalateTask,
  fetchCurrentUserId,
  fetchPatient,
  fetchSessionTeam,
  reopenPatient,
  reopenTask,
} from "~/lib/patients";
import { canAcknowledgePatient, canEditPatient, canRecordOvernightUpdate } from "~/lib/permissions";
import { cn } from "~/lib/utils";
import NotFoundRoute from "~/routes/not-found";
import { AcknowledgementModal } from "~/session/patient-detail/acknowledgement-modal";
import { AddTaskForm } from "~/session/patient-detail/add-task-form";
import { EscalatePatientDialog } from "~/session/patient-detail/escalate-patient-dialog";
import { EscalateTaskDialog } from "~/session/patient-detail/escalate-task-dialog";
import { NotesPanel } from "~/session/patient-detail/notes-panel";
import { useSession } from "~/session/session-context";

type ScreenState =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      patient: PatientResponseDto;
      currentUserId: string | null;
      canEdit: boolean;
      canAcknowledge: boolean;
      canRecordOvernight: boolean;
      acknowledgedBy: string | null;
      handedOverBy: string | null;
    };

// Human-readable form of `acknowledgedAt` for the green badge — date + 24h
// time, since the audit row may be hours or days later than the page load.
// en-GB so "30 May 2026, 13:42" rather than the locale-default "May 30,
// 2026, 1:42 PM", which fits the rest of the clinical UI.
const ACKNOWLEDGED_AT_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function formatAcknowledgedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return ACKNOWLEDGED_AT_FORMATTER.format(date);
}

// Priority presentation mirrors the patient form's dropdown (E4.5): a coloured
// dot for at-a-glance scanning, with the full "High priority" phrase as the
// accessible name (the dot + short label are decorative).
const PRIORITY_META: Record<
  PatientResponseDto["priority"],
  { label: string; srLabel: string; dotClass: string }
> = {
  high: { label: "High", srLabel: "High priority", dotClass: "bg-red-500" },
  medium: { label: "Medium", srLabel: "Medium priority", dotClass: "bg-amber-500" },
  low: { label: "Low", srLabel: "Low priority", dotClass: "bg-green-500" },
};

// The four SBAR blocks, in order, with the full name the Medical Council mandate
// (and E4.2) uses.
const SBAR_BLOCKS: readonly {
  key: keyof PatientResponseDto["sbar"];
  letter: string;
  label: string;
}[] = [
  { key: "situation", letter: "S", label: "Situation" },
  { key: "background", letter: "B", label: "Background" },
  { key: "assessment", letter: "A", label: "Assessment" },
  { key: "recommendation", letter: "R", label: "Recommendation" },
];

// Tasks are grouped most → least urgent so the incoming team reads the urgent
// jobs first.
const URGENCY_GROUPS: readonly {
  value: PatientResponseDto["tasks"][number]["urgency"];
  label: string;
}[] = [
  { value: "urgent", label: "Urgent" },
  { value: "soon", label: "Soon" },
  { value: "routine", label: "Routine" },
];

/**
 * The read-only patient detail view (E5.2): what the incoming doctor reads during
 * handover and the outgoing doctor reviews while writing. Rendered inside
 * `<SessionLayout>`, so the session (and the caller's ward access) is already
 * resolved — anyone with session access may read a patient; the API enforces the
 * same.
 *
 * It loads the patient, the caller's id, and the ward team (to name the outgoing
 * doctor in the handed-over footer), then resolves whether the caller may edit
 * (outgoing doctor, non-locked session, unacknowledged patient) and whether
 * they may acknowledge (incoming doctor, active session, unacknowledged
 * patient). The Acknowledge button opens the E7.1 legal-warning modal; the
 * actual `POST /patients/:id/acknowledge` call lands in E7.2 (HAN-56).
 */
export function PatientDetailScreen(): JSX.Element {
  const { session, markSessionLocked } = useSession();
  const { id: sessionId, patientId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [ackOpen, setAckOpen] = useState(false);
  // The task currently being escalated (HAN-79), or null when the dialog is closed.
  const [escalateTarget, setEscalateTarget] = useState<{ id: string; description: string } | null>(
    null,
  );
  // Whether the whole-patient escalation dialog is open (HAN-80).
  const [escalatePatientOpen, setEscalatePatientOpen] = useState(false);

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      if (!sessionId || !patientId) {
        setState({ kind: "not-found" });
        return;
      }
      setState({ kind: "loading" });

      const [currentUserId, patientResult, teamResult] = await Promise.all([
        fetchCurrentUserId(),
        fetchPatient(sessionId, patientId),
        fetchSessionTeam(sessionId),
      ]);
      if (signal.cancelled) return;

      if (patientResult.status === "not-found") {
        setState({ kind: "not-found" });
        return;
      }
      if (patientResult.status === "error") {
        setState({ kind: "error", message: patientResult.message });
        return;
      }

      const patient = patientResult.data;

      // Only the outgoing doctor can edit, and only while the session is open and
      // the patient is unacknowledged — the shared rule the edit screen (E5.3) and
      // the API enforce.
      const user = currentUserId ? { id: currentUserId } : null;
      const canEdit = canEditPatient(user, session, patient);
      // The incoming doctor acknowledges on an active session for a patient
      // who hasn't been acknowledged yet (shared rule from ~/lib/permissions;
      // the API enforces the same).
      const canAcknowledge = canAcknowledgePatient(user, session, patient);
      // The incoming doctor records the end-of-shift overnight update (HAN-68).
      // Available regardless of acknowledgement/lock — it's written during the
      // shift, after the handover has been received.
      const canRecordOvernight = canRecordOvernightUpdate(user, session);

      // Name the doctor who handed this patient over (the session's outgoing
      // doctor) and — once an acknowledgement is recorded — the incoming doctor
      // who signed it off. Both names come from the ward team; a lookup miss
      // just drops that name rather than failing the page.
      const team = teamResult.status === "ok" ? teamResult.data : [];
      const handedOverBy =
        patient.acknowledgedAt != null
          ? (team.find((m) => m.id === session.outgoingDoctorId)?.fullName ?? null)
          : null;
      const acknowledgedBy =
        patient.acknowledgedAt != null && patient.acknowledgedById != null
          ? (team.find((m) => m.id === patient.acknowledgedById)?.fullName ?? null)
          : null;

      setState({
        kind: "ready",
        patient,
        currentUserId,
        canEdit,
        canAcknowledge,
        canRecordOvernight,
        acknowledgedBy,
        handedOverBy,
      });
    },
    [patientId, session, sessionId],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  if (state.kind === "loading") {
    return (
      <div className="flex items-center justify-center py-16" aria-live="polite">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading patient"
        />
      </div>
    );
  }

  if (state.kind === "not-found") {
    return <NotFoundRoute />;
  }

  if (state.kind === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{state.message}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const {
    patient,
    currentUserId,
    canEdit,
    canAcknowledge,
    canRecordOvernight,
    acknowledgedBy,
    handedOverBy,
  } = state;
  const priority = PRIORITY_META[patient.priority];
  // Narrow `acknowledgedAt` to non-null here so the badge branch can format it
  // without re-asserting non-null on every reference.
  const acknowledgedAt = patient.acknowledgedAt;

  /**
   * The acknowledge handler the modal awaits. Specific server outcomes
   * (session locked, already acked, stale role) close the modal and toast a
   * targeted message — there's nothing the doctor can do by retrying in any
   * of those cases. Only the generic `error` branch throws, which keeps the
   * modal open so the doctor can retry; the modal then surfaces its own
   * generic failure toast (see {@link AcknowledgementModal}).
   */
  const handleAcknowledge = async (): Promise<void> => {
    if (!sessionId) return;
    const result = await acknowledgePatient(sessionId, patient.id);
    if (result.status === "ok") {
      if (result.data.sessionLocked) {
        // Final patient — the session is now locked. Flip the SessionLayout's
        // session state first so the lock banner + hidden edit affordances
        // appear in the same frame, then pop the user back to the patient
        // list so the "done" view is what they read, not the detail page.
        markSessionLocked();
        toast.success("Handover complete. The session is now locked.");
        navigate(`/session/${sessionId}`);
      } else {
        toast.success("Patient acknowledged.");
        setReloadKey((k) => k + 1);
      }
      return;
    }
    if (result.status === "session_locked") {
      // Race against another doctor's final ack — the session is already
      // locked when our 423 lands. Flip the context so the list landing
      // renders the lock banner without a second fetch.
      markSessionLocked();
      toast.error("This handover has just been locked.");
      navigate(`/session/${sessionId}`);
      return;
    }
    if (result.status === "already_acked") {
      // Likely a stale tab — the page reload picks up the acknowledged state.
      toast.info("This patient has already been acknowledged.");
      setReloadKey((k) => k + 1);
      return;
    }
    if (result.status === "not_found") {
      toast.error("This patient is no longer available on the handover.");
      navigate(`/session/${sessionId}`);
      return;
    }
    // not_incoming / error — defer to the modal's generic retry-toast path.
    throw new Error(result.status);
  };

  /**
   * Escalate the task in `escalateTarget` to the ward's senior clinician(s)
   * (HAN-79). On success, toast who was notified and reload so the escalated
   * badge/banner appear. `already_escalated` is a stale-tab case the reload
   * resolves; `session_locked` navigates back; `not_found`/`error` throw so the
   * dialog stays open for a retry.
   */
  const handleEscalate = async (reason: string): Promise<void> => {
    if (!sessionId || !escalateTarget) return;
    const result = await escalateTask(sessionId, patient.id, escalateTarget.id, reason);
    if (result.status === "ok") {
      const names = result.data.notified.map((n) => n.fullName);
      toast.success(
        names.length > 0
          ? `Escalated to ${names.join(", ")}.`
          : "Escalated — no senior is currently available on this ward.",
      );
      setReloadKey((k) => k + 1);
      return;
    }
    if (result.status === "already_escalated") {
      toast.info("This task has already been escalated.");
      setReloadKey((k) => k + 1);
      return;
    }
    if (result.status === "session_locked") {
      markSessionLocked();
      toast.error("This handover has just been locked.");
      navigate(`/session/${sessionId}`);
      return;
    }
    // not_found / error — defer to the dialog's generic retry-toast path.
    throw new Error(result.status);
  };

  /**
   * Escalate the whole patient to the ward's senior clinician(s) (HAN-80). On
   * success, toast who was notified and reload so the escalated indicator
   * appears. `already_escalated` is a stale-tab case the reload resolves;
   * `not_found` navigates back; `error` throws so the dialog stays open for a
   * retry.
   */
  const handleEscalatePatient = async (reason: string): Promise<void> => {
    if (!sessionId) return;
    const result = await escalatePatient(sessionId, patient.id, reason);
    if (result.status === "ok") {
      const names = result.data.notified.map((n) => n.fullName);
      toast.success(
        names.length > 0
          ? `Patient escalated to ${names.join(", ")}.`
          : "Patient escalated — no senior is currently available on this ward.",
      );
      setReloadKey((k) => k + 1);
      return;
    }
    if (result.status === "already_escalated") {
      toast.info("This patient has already been escalated.");
      setReloadKey((k) => k + 1);
      return;
    }
    if (result.status === "not_found") {
      toast.error("This patient is no longer available on the handover.");
      navigate(`/session/${sessionId}`);
      return;
    }
    // error — defer to the dialog's generic retry-toast path.
    throw new Error(result.status);
  };

  // Whole-patient escalation is an emergency action any ward member may take,
  // but only while there's something to escalate: not once the patient is
  // acknowledged (signed-off) or already escalated, and not on a locked session.
  const patientEscalated = patient.escalatedAt != null;
  const canEscalatePatient =
    !patientEscalated && acknowledgedAt == null && session.status !== "locked";

  // Task 15: complete/re-open a single task.
  const handleCompleteTask = async (taskId: string): Promise<void> => {
    if (!sessionId) return;
    const result = await completeTask(sessionId, patient.id, taskId);
    if (result.status === "ok") {
      setState((prev) => (prev.kind === "ready" ? { ...prev, patient: result.patient } : prev));
      return;
    }
    if (result.status === "conflict") {
      toast.error("This task is already completed.");
      return;
    }
    toast.error(result.status === "error" ? result.message : "Could not complete task.");
  };

  const handleReopenTask = async (taskId: string): Promise<void> => {
    if (!sessionId) return;
    const result = await reopenTask(sessionId, patient.id, taskId);
    if (result.status === "ok") {
      setState((prev) => (prev.kind === "ready" ? { ...prev, patient: result.patient } : prev));
      return;
    }
    if (result.status === "conflict") {
      toast.error("This task is already open.");
      return;
    }
    toast.error(result.status === "error" ? result.message : "Could not re-open task.");
  };

  // Task 16: change priority.
  const handleChangePriority = async (newPriority: Priority): Promise<void> => {
    if (!sessionId) return;
    const result = await changePriority(sessionId, patient.id, newPriority);
    if (result.status === "ok") {
      setState((prev) => (prev.kind === "ready" ? { ...prev, patient: result.patient } : prev));
      return;
    }
    if (result.status === "error") {
      toast.error(result.message);
      return;
    }
    toast.error("Could not change priority. Please try again.");
  };

  // Task 16: mark patient complete / re-open.
  const handleCompletePatient = async (): Promise<void> => {
    if (!sessionId) return;
    const result = await completePatient(sessionId, patient.id);
    if (result.status === "ok") {
      setState((prev) => (prev.kind === "ready" ? { ...prev, patient: result.patient } : prev));
      return;
    }
    if (result.status === "conflict") {
      toast.error("This patient is already marked complete.");
      setReloadKey((k) => k + 1);
      return;
    }
    toast.error(result.status === "error" ? result.message : "Could not mark patient complete.");
  };

  const handleReopenPatient = async (): Promise<void> => {
    if (!sessionId) return;
    const result = await reopenPatient(sessionId, patient.id);
    if (result.status === "ok") {
      setState((prev) => (prev.kind === "ready" ? { ...prev, patient: result.patient } : prev));
      return;
    }
    if (result.status === "conflict") {
      toast.error("This patient is not marked complete.");
      setReloadKey((k) => k + 1);
      return;
    }
    toast.error(result.status === "error" ? result.message : "Could not re-open patient.");
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/session/${sessionId}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to patients
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">{patient.fullName}</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>Bed {patient.bedNumber}</span>
          <span aria-hidden="true">·</span>
          <span>
            Hospital number <span className="font-mono">{patient.hospitalNumber}</span>
          </span>
          {/* Task 16: interactive priority select; replaces the read-only pill. */}
          <div className="flex items-center gap-1.5">
            <span aria-hidden="true" className={cn("h-2 w-2 rounded-full", priority.dotClass)} />
            <Select
              value={patient.priority}
              onValueChange={(v) => void handleChangePriority(v as Priority)}
            >
              <SelectTrigger
                aria-label="Priority"
                className="h-auto border-0 bg-transparent p-0 text-sm font-medium text-foreground shadow-none hover:bg-muted/50 focus:ring-0"
                data-testid="priority-select"
              >
                <SelectValue />
                <span className="sr-only">{priority.srLabel}</span>
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {PRIORITY_META[p].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {patientEscalated ? (
            // HAN-80: the escalated indicator on the patient. Amber to match the
            // task-escalated badge and the escalated banner.
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-2.5 py-0.5 font-semibold text-white"
              data-testid="patient-escalated-badge"
            >
              <ArrowUpCircle className="h-3.5 w-3.5" aria-hidden="true" />
              Escalated
            </span>
          ) : null}
        </div>
      </header>

      {patient.alert ? (
        // A full-width red strip the spec calls "visually impossible to miss".
        // `role="alert"` so screen readers announce it too.
        <div
          role="alert"
          className="flex items-center gap-3 rounded-md bg-red-600 px-4 py-3 text-base font-semibold text-white shadow-sm"
        >
          <TriangleAlert className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{patient.alert}</span>
        </div>
      ) : null}

      <section className="space-y-4">
        <h2 className="text-lg font-medium">SBAR handover</h2>
        <dl className="space-y-4">
          {SBAR_BLOCKS.map((block) => (
            <div key={block.key} className="rounded-lg border border-border p-4">
              <dt className="text-sm font-semibold tracking-tight">
                <span className="text-muted-foreground">{block.letter} — </span>
                {block.label}
              </dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {patient.sbar[block.key]}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-medium">Tasks</h2>
        {patient.tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tasks for the incoming team.</p>
        ) : (
          <div className="space-y-4">
            {URGENCY_GROUPS.map((group) => {
              const tasks = patient.tasks.filter((task) => task.urgency === group.value);
              if (tasks.length === 0) return null;
              return (
                <div key={group.value} className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">{group.label}</h3>
                  <ul className="space-y-1.5">
                    {tasks.map((task) => {
                      const done = task.completedAt != null;
                      // HAN-78: notifiedAt is stamped by the overdue scan; it is
                      // the single source of truth for the red overdue state.
                      // Exclude acknowledged patients — their tasks are signed-off
                      // and no longer actionable (backend scan also excludes them).
                      const overdue =
                        !done && task.notifiedAt != null && patient.acknowledgedAt == null;
                      // HAN-82: a task copied from the previous shift. Its
                      // provenance is the "overdue from the start" flag — while
                      // still open it carries a distinct "Carried over" badge so
                      // the day team can see what didn't get done overnight.
                      const carried =
                        !done && task.carriedFromTaskId != null && patient.acknowledgedAt == null;
                      // HAN-79: escalatedAt is the single source of truth for the
                      // escalated state. Once escalated, the badge replaces the
                      // escalate affordance. Escalating is blocked on a locked
                      // session or an acknowledged patient (nothing left to chase).
                      const escalated = task.escalatedAt != null;
                      const canEscalate =
                        !done &&
                        !escalated &&
                        patient.acknowledgedAt == null &&
                        session.status !== "locked";
                      return (
                        <li key={task.id} className="flex items-start gap-2.5">
                          {/* Task 15: interactive checkbox — completes or re-opens the task. */}
                          <input
                            type="checkbox"
                            checked={done}
                            readOnly={false}
                            aria-label={task.description}
                            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border"
                            onChange={() => {
                              if (done) {
                                void handleReopenTask(task.id);
                              } else {
                                void handleCompleteTask(task.id);
                              }
                            }}
                          />
                          <span
                            className={cn(
                              "text-sm",
                              done
                                ? "text-muted-foreground line-through"
                                : overdue
                                  ? "font-medium text-priority-high"
                                  : "text-foreground",
                            )}
                          >
                            {task.description}
                            {carried ? (
                              <span
                                className="ml-2 rounded bg-indigo-600 px-1.5 py-0.5 text-xs font-semibold text-white"
                                data-testid="task-carried-over-badge"
                              >
                                Carried over
                              </span>
                            ) : null}
                            {overdue ? (
                              <span
                                className="ml-2 rounded bg-priority-high px-1.5 py-0.5 text-xs font-semibold text-white"
                                data-testid="task-overdue-badge"
                              >
                                Overdue
                              </span>
                            ) : null}
                            {escalated ? (
                              <span
                                className="ml-2 rounded bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white"
                                data-testid="task-escalated-badge"
                              >
                                Escalated
                              </span>
                            ) : null}
                          </span>
                          <div className="ml-auto flex shrink-0 gap-1">
                            {/* Task 15: named complete/re-open button alongside the escalate button. */}
                            {done ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                                aria-label={`Re-open ${task.description}`}
                                onClick={() => void handleReopenTask(task.id)}
                              >
                                Re-open
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                                aria-label={`Complete ${task.description}`}
                                onClick={() => void handleCompleteTask(task.id)}
                              >
                                <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                                Complete
                              </Button>
                            )}
                            {canEscalate ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto shrink-0 px-2 py-0.5 text-xs text-amber-700 hover:text-amber-800"
                                data-testid="escalate-task-button"
                                onClick={() =>
                                  setEscalateTarget({ id: task.id, description: task.description })
                                }
                              >
                                <ArrowUpCircle className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                                Escalate
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        {/* Task 15: add a new task inline. */}
        {sessionId ? (
          <AddTaskForm
            sessionId={sessionId}
            patientId={patient.id}
            onAdded={(updatedPatient) => {
              setState((prev) =>
                prev.kind === "ready" ? { ...prev, patient: updatedPatient } : prev,
              );
            }}
          />
        ) : null}
      </section>

      {/* Task 17: clinical notes panel. */}
      {sessionId ? (
        <section className="space-y-4">
          <h2 className="text-lg font-medium">Notes</h2>
          <NotesPanel sessionId={sessionId} patientId={patient.id} currentUserId={currentUserId} />
        </section>
      ) : null}

      <footer className="space-y-2 border-t border-border pt-4">
        {acknowledgedAt != null ? (
          // E7.3 — the badge replaces the Acknowledge button once the
          // sign-off is recorded. `role="status"` so screen readers announce
          // the new state after an optimistic post-ack rerender; deliberately
          // a div + no hover state so it doesn't read as a control.
          <div
            role="status"
            data-testid="acknowledged-badge"
            className="inline-flex items-center gap-2 rounded-md bg-priority-low/10 px-3 py-2 text-sm font-medium text-priority-low"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            <span>
              {acknowledgedBy
                ? `Acknowledged by ${acknowledgedBy} at ${formatAcknowledgedAt(acknowledgedAt)}`
                : `Acknowledged at ${formatAcknowledgedAt(acknowledgedAt)}`}
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            {canAcknowledge ? (
              <Button type="button" onClick={() => setAckOpen(true)}>
                Acknowledge handover
              </Button>
            ) : null}
            {canEdit ? (
              <Button asChild>
                <Link to={`/session/${sessionId}/patient/${patient.id}/edit`}>Edit patient</Link>
              </Button>
            ) : null}
            {canEscalatePatient ? (
              <Button
                type="button"
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                data-testid="escalate-patient-button"
                onClick={() => setEscalatePatientOpen(true)}
              >
                <ArrowUpCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                Escalate patient
              </Button>
            ) : null}
            {/* Task 16: mark-complete / re-open the patient. */}
            {patient.archivedAt == null ? (
              <Button type="button" variant="outline" onClick={() => void handleCompletePatient()}>
                Mark complete
              </Button>
            ) : (
              <>
                <span className="text-sm text-muted-foreground">
                  Completed by {patient.archivedBy?.fullName ?? "unknown"}
                </span>
                <Button type="button" variant="outline" onClick={() => void handleReopenPatient()}>
                  Re-open
                </Button>
              </>
            )}
          </div>
        )}
        {canRecordOvernight ? (
          // HAN-68: the incoming doctor's end-of-shift overnight update. Shown
          // regardless of acknowledgement/lock — it's written during the shift,
          // after handover. Outlined so it reads as secondary to acknowledge.
          <div>
            <Button asChild variant="outline" data-testid="overnight-update-button">
              <Link to={`/session/${sessionId}/patient/${patient.id}/overnight-update`}>
                <Moon className="mr-2 h-4 w-4" aria-hidden="true" />
                Overnight update
              </Link>
            </Button>
          </div>
        ) : null}
        {handedOverBy ? (
          <p className="text-sm text-muted-foreground">Handed over by {handedOverBy}</p>
        ) : null}
      </footer>

      {canAcknowledge ? (
        <AcknowledgementModal
          patient={{ fullName: patient.fullName, bedNumber: patient.bedNumber }}
          open={ackOpen}
          onOpenChange={setAckOpen}
          onConfirm={handleAcknowledge}
        />
      ) : null}

      <EscalateTaskDialog
        task={escalateTarget}
        open={escalateTarget != null}
        onOpenChange={(open) => {
          if (!open) setEscalateTarget(null);
        }}
        onConfirm={handleEscalate}
      />

      <EscalatePatientDialog
        patient={{ fullName: patient.fullName, bedNumber: patient.bedNumber }}
        open={escalatePatientOpen}
        onOpenChange={setEscalatePatientOpen}
        onConfirm={handleEscalatePatient}
      />
    </div>
  );
}
